import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useActiveSessionsStore } from '../stores/activeSessionsStore';
import { useConversationAffichee } from '../state/conversationAffichee';
import type { SessionStatus } from '../types/session.types';

// ============================================================================
// Component
// ============================================================================

/**
 * BackgroundSessionNotifier — renders nothing, fires toast notifications.
 *
 * Subscribes to the Zustand store via the raw `.subscribe` API (not a selector
 * hook) so the component itself never re-renders on state changes — only the
 * toast layer updates.
 *
 * **Monté par la coque depuis #859**, plus par l'écran assistant. Il ne vivait
 * que là, donc quitter cet écran supprimait tout avertissement de fin de tour —
 * exactement ce que #856 et #857 ont corrigé pour le travail lui-même. Deux
 * conséquences de ce déplacement, toutes deux voulues :
 *
 * 1. **La suppression se fait sur la conversation AFFICHÉE**, plus sur une
 *    `sessionKey` locale à l'assistant. Une clé seule n'est jamais une identité
 *    (elle peut encore désigner A pendant un rendu où l'URL vaut B), et surtout
 *    la coque n'en a aucune : ce qu'elle sait, c'est ce que l'avocat regarde —
 *    y compris le chat embarqué d'un dossier, qui choisit son fil sans toucher
 *    à l'URL.
 * 2. **Plus de `Set` de dédoublonnage permanent.** Il était sans effet tant que
 *    le composant se remontait à chaque venue sur l'assistant ; monté une fois
 *    pour l'onglet, il aurait rendu muet tout tour suivant le premier d'une même
 *    conversation. La détection est déjà un **front** (`streaming → ready`) et
 *    l'instantané est mis à jour avant tout `continue` : elle ne peut pas se
 *    déclencher deux fois pour le même tour.
 */
export function BackgroundSessionNotifier(): null {
  const navigate = useNavigate();
  const conversationAffichee = useConversationAffichee();

  // Ref lue par la closure d'abonnement : elle doit voir la valeur courante sans
  // que l'abonnement soit refait à chaque navigation.
  const conversationAfficheeRef = useRef<string | null>(conversationAffichee);
  useEffect(() => {
    conversationAfficheeRef.current = conversationAffichee;
  }, [conversationAffichee]);

  useEffect(() => {
    // Snapshot previous statuses so we can detect transitions.
    const prevStatuses = new Map<string, SessionStatus>();

    // Seed initial statuses to avoid spurious toasts for existing sessions on
    // first mount (e.g. after a navigation).
    const seed = useActiveSessionsStore.getState();
    for (const [key, session] of Object.entries(seed.sessions)) {
      prevStatuses.set(key, session.status);
    }

    const unsubscribe = useActiveSessionsStore.subscribe((state) => {
      const { sessions } = state;

      for (const [sessionKey, session] of Object.entries(sessions)) {
        const prev = prevStatuses.get(sessionKey);
        const next = session.status;

        // Update our local snapshot before any early-return so the next cycle
        // always compares against the most recent status.
        prevStatuses.set(sessionKey, next);

        // No transition to detect yet (brand-new session).
        if (prev === undefined || prev === next) {
          continue;
        }

        // Aucune notification pour le fil que l'avocat a sous les yeux.
        if (session.conversationId && session.conversationId === conversationAfficheeRef.current) {
          continue;
        }

        // ---- streaming → ready ------------------------------------------------
        if (prev === 'streaming' && next === 'ready') {
          const { conversationId } = session;

          toast.success('Réponse prête', {
            action: {
              label: 'Voir',
              onClick: () => {
                // The active conversation is the URL ?c=. AgentV3View reattaches
                // the live session + reconstructs the case from the store.
                if (conversationId) navigate(`/assistant?c=${conversationId}`);
                else navigate('/assistant');
              },
            },
          });
          continue;
        }

        // ---- streaming → error -----------------------------------------------
        if (prev === 'streaming' && next === 'error') {
          toast.error(
            "Une erreur est survenue dans une conversation en arrière-plan",
          );
          continue;
        }
      }

      // Clean up entries for sessions that have been removed from the store so
      // prevStatuses doesn't grow unboundedly across long sessions.
      for (const key of prevStatuses.keys()) {
        if (!(key in sessions)) {
          prevStatuses.delete(key);
        }
      }
    });

    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once — la conversation affichée passe par la ref

  return null;
}
